import i18n from "../i18n";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Baby, Syringe, Stethoscope, ShieldCheck, Heart, TrendingUp,
    AlertTriangle, ShoppingCart, Package, Skull, Users, Activity,
} from "lucide-react";
import { toast } from "sonner";

const PIPELINE = [
    { key: "newborn", label: "Newborn", icon: Baby, color: "bg-pink-500/10 border-pink-500/30" },
    { key: "brooding", label: "Brooding", icon: Baby, color: "bg-amber-500/10 border-amber-500/30" },
    { key: "suckling", label: "Suckling", icon: Baby, color: "bg-pink-500/10 border-pink-500/30" },
    { key: "weaned", label: "Weaned", icon: Users, color: "bg-blue-500/10 border-blue-500/30" },
    { key: "growing", label: "Growing", icon: TrendingUp, color: "bg-blue-500/10 border-blue-500/30" },
    { key: "finishing", label: "Finishing", icon: TrendingUp, color: "bg-primary/10 border-primary/30" },
    { key: "ready_for_sale", label: "Ready for Sale", icon: ShieldCheck, color: "bg-primary/15 border-primary/40" },
    { key: "reserved", label: "Reserved", icon: Package, color: "bg-purple-500/10 border-purple-500/30" },
    { key: "sold", label: "Sold", icon: ShoppingCart, color: "bg-secondary border-border" },
];

export default function BirthToSalePage() {
    const [data, setData] = useState(null);
    const [filterSpecies, setFilterSpecies] = useState("all");
    const [species, setSpecies] = useState([]);
    const [dragOverCol, setDragOverCol] = useState(null);
    const [blockerDialog, setBlockerDialog] = useState(null);

    const load = useCallback(async () => {
        try {
            const [d, s] = await Promise.all([
                api.get("/birth-to-sale/summary").then((r) => r.data),
                api.get("/species").then((r) => r.data),
            ]);
            setData(d); setSpecies(s);
        } catch (err) {
            toast.error("Failed to load dashboard");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const moveAnimal = async (animalId, targetStage) => {
        try {
            await api.post(`/animals/${animalId}/set-life-stage`, { stage: targetStage, reason: "Moved via kanban" });
            toast.success(`Moved to ${targetStage.replace(/_/g, " ")}`);
            load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            // If backend blocked with blockers array, show dialog
            if (detail && typeof detail === "object" && detail.blockers) {
                setBlockerDialog({ animalId, targetStage, ...detail });
            } else {
                toast.error(formatApiErrorDetail(detail));
            }
        }
    };

    const forceMove = async () => {
        if (!blockerDialog) return;
        try {
            await api.post(`/animals/${blockerDialog.animalId}/set-life-stage`, {
                stage: blockerDialog.targetStage, force: true, reason: "Force-moved via kanban (blockers overridden)",
            });
            toast.success("Force-moved");
            setBlockerDialog(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    if (!data) return <div className="p-6 text-muted-foreground" data-testid="bts-loading">{i18n.t("batch2.33ce417454bf")}</div>;
    const k = data.kpis;

    const filterCard = (list) => filterSpecies === "all"
        ? list
        : list.filter((a) => a.species_id === filterSpecies);

    return (
        <div className="space-y-6" data-testid="birth-to-sale-page">
            <div>
                <div className="tiny-label">{i18n.t("batch2.5bdd5c241b30")}</div>
                <h1 className="text-3xl font-bold mt-1">{i18n.t("batch2.c813d87308e6")}</h1>
                <p className="text-sm text-muted-foreground">
                    {i18n.t("batch2.2d812736dca6")}
                </p>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="bts-kpis">
                <KpiCard icon={Heart} label={i18n.t("batch2.f4d52fad548f")} value={formatNumber(k.total_alive)} testid="bts-kpi-alive" tone="green" />
                <KpiCard icon={Baby} label={i18n.t("batch2.2ee822bf17dd")} value={formatNumber(k.births_this_month)} testid="bts-kpi-births" tone="pink" />
                <KpiCard icon={Baby} label={i18n.t("batch2.096e170e0991")} value={formatNumber(k.newborn)} testid="bts-kpi-newborn" tone="pink" />
                <KpiCard icon={TrendingUp} label={i18n.t("batch2.c195df524d2c")} value={formatNumber(k.growing)} testid="bts-kpi-growing" tone="blue" />
                <KpiCard icon={Stethoscope} label={i18n.t("batch2.8b7cad5bca5b")} value={formatNumber(k.active_treatments)} testid="bts-kpi-treatments" tone={k.active_treatments > 0 ? "amber" : "slate"} />
                <KpiCard icon={Syringe} label={i18n.t("batch2.b298bc6a9e73")} value={formatNumber(k.vacc_due_today + k.vacc_overdue)} testid="bts-kpi-vacc-due" tone={k.vacc_overdue > 0 ? "red" : k.vacc_due_today > 0 ? "amber" : "slate"} sub={k.vacc_overdue ? `${k.vacc_overdue} overdue` : null} />
                <KpiCard icon={ShieldCheck} label={i18n.t("batch2.c9411ae8d103")} value={formatNumber(k.ready_for_sale)} testid="bts-kpi-ready" tone="green" />
                <KpiCard icon={Package} label={i18n.t("batch2.67a6ff10f1b9")} value={formatNumber(k.reserved)} testid="bts-kpi-reserved" tone="purple" />
                <KpiCard icon={ShoppingCart} label={i18n.t("batch2.dcf09d387818")} value={formatNumber(k.sold_this_month)} testid="bts-kpi-sold" tone="slate" />
                <KpiCard icon={Skull} label={i18n.t("batch2.7ec2484371b6")} value={formatNumber(k.mortality_this_month)} testid="bts-kpi-mortality" tone={k.mortality_this_month > 0 ? "red" : "slate"} />
            </div>

            {/* Species filter */}
            {species.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="bts-species-filter-row">
                    <Button size="sm" variant={filterSpecies === "all" ? "default" : "outline"} onClick={() => setFilterSpecies("all")} data-testid="bts-species-all">
                        {i18n.t("batch2.c500690deb24")}
                    </Button>
                    {species.map((s) => (
                        <Button
                            key={s.id}
                            size="sm"
                            variant={filterSpecies === s.id ? "default" : "outline"}
                            onClick={() => setFilterSpecies(s.id)}
                            data-testid={`bts-species-${s.id}`}
                        >
                            {s.icon} {s.name}
                        </Button>
                    ))}
                </div>
            )}

            {/* Kanban pipeline */}
            <div className="overflow-x-auto pb-2">
                <div className="flex gap-3 min-w-max" data-testid="bts-kanban">
                    {PIPELINE.map((col) => {
                        const list = filterCard(data.pipeline[col.key] || []);
                        const Icon = col.icon;
                        const isDragOver = dragOverCol === col.key;
                        return (
                            <div
                                key={col.key}
                                className={`w-[240px] flex-shrink-0 rounded-xl border ${col.color} p-3 space-y-2 transition ${isDragOver ? "ring-2 ring-primary ring-offset-2 scale-[1.02]" : ""}`}
                                data-testid={`bts-column-${col.key}`}
                                onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
                                onDragLeave={() => setDragOverCol(null)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const animalId = e.dataTransfer.getData("text/plain");
                                    setDragOverCol(null);
                                    if (animalId) moveAnimal(animalId, col.key);
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <Icon className="w-4 h-4" />
                                        <span className="font-semibold text-sm">{col.label}</span>
                                    </div>
                                    <Badge variant="outline" data-testid={`bts-count-${col.key}`}>{list.length}</Badge>
                                </div>
                                <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
                                    {list.length === 0 && (
                                        <div className="text-xs text-muted-foreground py-2 text-center">{i18n.t("batch2.ad87109bfff0")}</div>
                                    )}
                                    {list.map((a) => (
                                        <div
                                            key={a.id}
                                            draggable
                                            onDragStart={(e) => e.dataTransfer.setData("text/plain", a.id)}
                                            className="block rounded-lg bg-white/70 hover:bg-white border border-border/60 px-2 py-1.5 text-xs transition cursor-grab active:cursor-grabbing"
                                            data-testid={`bts-card-${a.id}`}
                                        >
                                            <Link to={`/animals/${a.id}`} className="block">
                                                <div className="font-semibold truncate">{a.tag_id || a.name || "—"}</div>
                                                <div className="text-[10px] text-muted-foreground truncate">
                                                    {a.species_name || ""}{a.birth_date ? ` · ${a.birth_date}` : ""}
                                                </div>
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="text-xs text-muted-foreground -mt-2">
                <b>{i18n.t("batch2.2ee750e04190")}</b> {i18n.t("batch2.11a1827a2b21")}
            </div>

            {/* Quick alerts */}
            {(k.vacc_overdue > 0 || k.active_treatments > 0) && (
                <Card className="p-4 border-amber-500/40 bg-amber-500/5">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                        <div className="text-sm space-y-0.5">
                            {k.vacc_overdue > 0 && <div><b>{k.vacc_overdue}</b> {i18n.t("batch2.156310055287")}</div>}
                            {k.active_treatments > 0 && <div><b>{k.active_treatments}</b> {i18n.t("batch2.3f3cc34fc78f")}</div>}
                        </div>
                    </div>
                </Card>
            )}

            {blockerDialog && (
                <div
                    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
                    onClick={() => setBlockerDialog(null)}
                    data-testid="bts-blocker-dialog"
                >
                    <Card className="max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 text-destructive font-bold">
                            <AlertTriangle className="w-5 h-5" />
                            {i18n.t("batch2.68309f127cb6")}
                        </div>
                        <p className="text-sm">
                            {i18n.t("batch2.911f3b0b4732")} <b className="capitalize">{blockerDialog.targetStage.replace(/_/g, " ")}</b> {i18n.t("batch2.c0d66da0ed20")}
                        </p>
                        <ul className="list-disc pl-5 text-sm text-destructive space-y-1">
                            {blockerDialog.blockers.map((b) => (
                                <li key={b.key}><b>{b.label}</b> — {b.detail}</li>
                            ))}
                        </ul>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={() => setBlockerDialog(null)} data-testid="bts-blocker-cancel">{i18n.t("batch2.77dfd2135f4d")}</Button>
                            <Button variant="destructive" onClick={forceMove} data-testid="bts-blocker-force">
                                {i18n.t("batch2.c4506f67727e")}
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

const TONES = {
    green: "bg-primary/10 text-primary border-primary/30",
    pink: "bg-pink-500/10 text-pink-700 border-pink-500/30",
    blue: "bg-blue-500/10 text-blue-700 border-blue-500/30",
    purple: "bg-purple-500/10 text-purple-700 border-purple-500/30",
    amber: "bg-amber-500/10 text-amber-700 border-amber-500/30",
    red: "bg-destructive/10 text-destructive border-destructive/40",
    slate: "bg-secondary text-foreground border-border",
};
function KpiCard({ icon: Icon, label, value, sub, testid, tone = "slate" }) {
    return (
        <Card className={`p-3 border ${TONES[tone]}`} data-testid={testid}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80">
                <Icon className="w-3.5 h-3.5" />{label}
            </div>
            <div className="text-2xl font-bold mt-1">{value}</div>
            {sub && <div className="text-[10px] opacity-80">{sub}</div>}
        </Card>
    );
}
